import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Box } from "@mui/material";
import { toast } from "react-toastify";

import Loader from "../../../components/Loader";
import CommonDeleteModal from "../../../components/Shared/CommonDeleteModal";
import { emailTemplatesApi } from "../../../services/emailTemplates";
import TemplateFormModal from "./TemplateFormModal";
import TemplatesTable from "./TemplatesTable";
import CustomBreadcrumbs from "../../../components/CustomBreadcrumbs";

const EmailTemplates = () => {
  const { open } = useOutletContext();
  const [templates, setTemplates] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isSingleLoading, setIsSingleLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAllTemplates = async () => {
    try {
      setIsTableLoading(true);
      const response = await emailTemplatesApi.getAllEmailTemplates();
      console.log("fetchAllTemplates response", response);
      const templateList = Array.isArray(response)
        ? response
        : Array.isArray(response?.results)
          ? response.results
          : Array.isArray(response?.data)
            ? response.data
            : [];
      setTemplates(templateList);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to fetch email templates",
      );
    } finally {
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTemplates();
  }, []);

  const normalizedSearch = searchText.trim().toLowerCase();
  const filteredRows = useMemo(
    () =>
      templates.filter((row) => {
        if (!normalizedSearch) return true;

        return [row.template_id, row.template_subject, row.template_name].some(
          (value) => value.toLowerCase().includes(normalizedSearch),
        );
      }),
    [templates, normalizedSearch],
  );

  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setPage(0);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenCreateModal = () => {
    setFormMode("create");
    setSelectedTemplate(null);
    setIsFormModalOpen(true);
  };

  const handleCloseFormModal = (forceClose = false) => {
    if (isSubmitting && !forceClose) return;

    setIsFormModalOpen(false);
    setFormMode("create");
    setSelectedTemplate(null);
    setIsSingleLoading(false);
  };

  const handleEditTemplate = async (template) => {
    try {
      setFormMode("edit");
      setSelectedTemplate(null);
      setIsFormModalOpen(true);
      setIsSingleLoading(true);

      const response = await emailTemplatesApi.getSingleEmailTemplate(
        template.template_id,
      );
      console.log("getSingleEmailTemplate response", response);
      setSelectedTemplate(response);
    } catch (error) {
      setIsFormModalOpen(false);
      toast.error(
        error?.response?.data?.message || "Failed to fetch email template",
      );
    } finally {
      setIsSingleLoading(false);
    }
  };

  const handleSubmitTemplate = async (payload) => {
    try {
      setIsSubmitting(true);

      if (formMode === "edit" && selectedTemplate?.template_id) {
        await emailTemplatesApi.editEmailTemplate(
          payload,
          selectedTemplate.template_id,
        );
        toast.success("Email template updated successfully");
      } else {
        await emailTemplatesApi.addEmailTemplate(payload);
        toast.success("Email template created successfully");
      }

      handleCloseFormModal(true);
      await fetchAllTemplates();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${formMode === "edit" ? "update" : "create"} email template`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (template) => {
    setSelectedTemplate(template);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = (forceClose = false) => {
    if (isDeleting && !forceClose) return;

    setIsDeleteModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate?.template_id) return;

    try {
      setIsDeleting(true);
      await emailTemplatesApi.deleteEmailTemplate(selectedTemplate.template_id);
      toast.success("Email template deleted successfully");
      handleCloseDeleteModal(true);
      await fetchAllTemplates();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to delete email template",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {isTableLoading && <Loader />}

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          width: open ? "calc(100% - 240px)" : "calc(100% - 100px)",
          ml: open ? "240px" : "100px",
          transition: "margin 0.3s ease",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Box mt={9} mb={1}>
          <CustomBreadcrumbs currenttitle="Email Templates" />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#fff",
            borderRadius: 2,
            overflow: "hidden",
            height: "calc(100% - 73px)",
            marginLeft: open ? "" : "1em",
          }}
        >
          <TemplatesTable
            rows={paginatedRows}
            totalRows={filteredRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            searchText={searchText}
            onSearchChange={handleSearchChange}
            onAddTemplate={handleOpenCreateModal}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onEditTemplate={handleEditTemplate}
            onDeleteTemplate={handleOpenDeleteModal}
          />
        </Box>
      </Box>

      <TemplateFormModal
        open={isFormModalOpen}
        mode={formMode}
        template={selectedTemplate}
        isFetching={isSingleLoading}
        isSubmitting={isSubmitting}
        onClose={handleCloseFormModal}
        onSubmit={handleSubmitTemplate}
      />

      <CommonDeleteModal
        title="Delete Email Template"
        text="Are you sure you want to delete this email template?"
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteTemplate}
      />
    </>
  );
};

export default EmailTemplates;
